import React, { useState } from "react";
import hasPermission from "@/hooks/hasPermission";
import { Trash2, PenLine, Eye, Info, Archive, ArchiveRestore } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { noImg } from "@/assets";
import {
  ItemsDialog,
  DetailsDialog,
  DeleteConfirmDialog,
  ImageDialog,
  ArchiveConfirmDialog,
} from "@/components/dialog/common-dialog";
import {
  deleteSundayRoastById,
  clearSundayRoastMessages,
  updateExistingSundayRoast,
  fetchAllSundayRosts,
} from "@/redux/slice/menu-manage-slice/special-menu/sunday-roast-slice";
import { toast } from "react-hot-toast";
import { Switch } from "@/components/ui/switch";
import { toggleItemStatus } from "@/api/new-api/staff-management/menu-manage/toggle-status";
import useAuth from "@/hooks/useAuth";

function TableData({ data }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [isItemDialog, setisItemDialog] = useState(false);
  const [details, setDetails] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState("archive");
  const [imageDialog, setImageDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const { deleteLoading, updateLoading, requestStatus, error } = useSelector(
    (state) => state.menuManage.sundayRoast
  );
  const [deleteDialog, setDeleteDialog] = useState(false);

  const closeArchiveDialog = () => {
    setArchiveDialog(false);
  };

  const archiveHandler = async (id, item, type) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearSundayRoastMessages());
      const newStatus = type === "archive" ? "archived" : "active";
      const dataPayload = {
        type: "update",
        item_no: item?.item_no || null,
        name: item?.name || "",
        description: item?.description || "",
        price: item?.price || null,
        day_id: item?.day?.id || null,
        items: item?.items || [],
        vat: item?.vat ?? 0,
        status: newStatus,
        file_manager_id: item?.file_manager_id || item?.image?.id || undefined,
        sunday_roast_items: item?.sunday_roast_items?.map((sri) => ({
          item_id: sri?.item_id,
          item_type: sri?.item_type,
          variant_id: sri?.variant_id,
          file_manager_id: sri?.file_manager_id,
          quantity: sri?.quantity,
          price: sri?.price,
        })) || [],
        client_id: item?.client_id,
        restaurant_id: item?.restaurant_id,
      };
      const res = await dispatch(
        updateExistingSundayRoast({
          id,
          data: dataPayload,
        })
      );
      if (updateExistingSundayRoast.fulfilled.match(res)) {
        toast.success(`Sunday Roast archived successfully!`);
      } else if (updateExistingSundayRoast.rejected.match(res)) {
        toast.error(res?.payload || `Failed to archive Sunday Roast.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      closeArchiveDialog();
    }
  };

  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearSundayRoastMessages());
      const res = await dispatch(deleteSundayRoastById(id));
      if (deleteSundayRoastById.fulfilled.match(res)) {
        toast.success(`Sunday Roast deleted successfully!`);
      } else if (deleteSundayRoastById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to delete Sunday Roast.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setDeleteDialog(false);
      setItemDialog(null);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleItemStatus("sunday-roast", id);
      if (res.status === "success") {
        toast.success(res.message || "Sunday Roast status updated successfully");
        dispatch(fetchAllSundayRosts(user?.restaurant_id));
      } else {
        toast.error(res.message || "Failed to toggle status.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  return (
    <>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead>Item Name</TableHead>
            <TableHead>Image</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Items</TableHead>
            <TableHead>Day</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data && data.length > 0 ? (
            data.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">{item?.name}</TableCell>
                <TableCell>
                  {" "}
                  <img
                    onClick={() => {
                      setImageDialog(true);
                      setItemDialog(item);
                    }}
                    className="h-8 rounded-md w-8 object-cover cursor-pointer"
                    src={item?.file_manager_url || noImg}
                    alt="Item Image"
                  />{" "}
                </TableCell>
                <TableCell>${item?.price || "0"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {item?.sunday_roast_items?.length}
                    <Eye
                      className="cursor-pointer"
                      onClick={() => {
                        setisItemDialog(true);
                        setItemDialog(item);
                      }}
                      size={18}
                    />
                  </div>
                </TableCell>
                <TableCell>{item?.day?.name || "N/A"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {item?.status === "active" ? (
                      <>
                        <span className="text-cgreen text-base">Active</span>
                        <Switch
                          checked={true}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "inactive" ? (
                      <>
                        <span className="text-cred text-base">Inactive</span>
                        <Switch
                          checked={false}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "archived" ? (
                      <span className="text-cred text-base">Archived</span>
                    ) : (
                      <span className="text-cred text-base">Draft</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right justify-end flex gap-2 items-center">
                  <div className="flex items-center gap-2 h-8">
                    <Info
                      onClick={() => {
                        setDetails(true);
                        setItemDialog(item);
                      }}
                      className=" cursor-pointer"
                      size={18}
                    />
                    {hasPermission("back_office.menu_management.update_special_menu") && item?.status !== "archived" && (
                      <Link
                        state={item}
                        to={"/backoffice/menu/special-menu/sunday-roast/update"}
                        className="cursor-pointer"
                      >
                        <PenLine className="text-cyellow" size={18} />
                      </Link>
                    )}

                    {hasPermission("back_office.menu_management.delete_special_menu") && item?.status !== "archived" && (
                      <span>
                        {item?.status === "draft" ? (
                          <Trash2
                            onClick={() => {
                              setItemDialog(item);
                              setDeleteDialog(true);
                            }}
                            className="text-cred cursor-pointer"
                            size={18}
                          />
                        ) : (
                          <Archive
                            onClick={() => {
                              setArchiveActionType("archive");
                              setItemDialog(item);
                              setArchiveDialog(true);
                            }}
                            className="text-cred cursor-pointer"
                            size={18}
                          />
                        )}
                      </span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} className="text-center">
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <ItemsDialog
        items={itemDialog?.sunday_roast_items}
        isOpen={isItemDialog}
        onClose={() => setisItemDialog(false)}
      />

      <ImageDialog
        items={itemDialog}
        isOpen={imageDialog}
        onClose={() => setImageDialog(false)}
      />

      <DetailsDialog
        items={itemDialog}
        isOpen={details}
        onClose={() => setDetails(false)}
      />
      <ArchiveConfirmDialog
        isLoading={updateLoading}
        isOpen={archiveDialog}
        onClose={closeArchiveDialog}
        onConfirm={() => {
          archiveHandler(itemDialog.id, itemDialog, archiveActionType);
        }}
        title={archiveActionType === "archive" ? "Archive Sunday Roast?" : "Restore Sunday Roast?"}
        description={
          archiveActionType === "archive"
            ? `Are you sure you want to archive "${itemDialog?.name}"?`
            : `Are you sure you want to restore "${itemDialog?.name}"?`
        }
        isArchive={archiveActionType === "archive"}
      />
      <DeleteConfirmDialog
        isLoading={deleteLoading}
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setItemDialog(null);
        }}
        onConfirm={() => {
          deleteHandler(itemDialog.id);
        }}
        title="Delete Draft Sunday Roast?"
        description={`Are you sure you want to delete the draft Sunday Roast "${itemDialog?.name}"? This action cannot be undone.`}
      />
    </>
  );
}

export default TableData;
